import { createFileRoute } from '@tanstack/react-router'
import { Fragment, useState } from 'react'
import {
  ShieldCheck,
  UserPlus,
  Trash2,
  Plus,
  X,
  Check,
  Copy,
  Search,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { SuperAdminHeader } from '@/features/super-admin/components/SuperAdminHeader'
import { useMenuToggle } from '@/features/super-admin/super-admin-shell-context'
import { useAuth } from '@/features/auth'
import {
  useSuperAdmins,
  usePromoteSuperAdmin,
  useCreateSuperAdmin,
  useUpdateSuperAdminPermissions,
  useRevokeSuperAdmin,
  SuperAdmin,
  SuperAdminPermissions,
} from '@/features/super-admin/hooks/use-super-admins'
import { supabase } from '@/lib/supabase'
import { ConfirmModal } from '@/features/super-admin/components/ConfirmModal'
import { AlertModal } from '@/features/super-admin/components/AlertModal'

export const Route = createFileRoute('/super-admin/super-admins/')({
  component: SuperAdminsPage,
})

const PERMISSION_LABELS: Record<keyof SuperAdminPermissions, string> = {
  can_manage_organizations: 'Gerenciar Organizações',
  can_manage_billing: 'Gerenciar Cobrança',
  can_manage_plans: 'Gerenciar Planos',
  can_manage_users: 'Gerenciar Usuários',
  can_impersonate: 'Acessar como Usuário',
  can_access_audit_logs: 'Ver Logs de Auditoria',
  can_manage_config: 'Gerenciar Configurações',
}

function SuperAdminsPage() {
  const { onMenuToggle } = useMenuToggle()
  const { data: superAdmins, isLoading, error: loadError, refetch } = useSuperAdmins()
  const { userProfile } = useAuth()

  const [showAddModal, setShowAddModal] = useState(false)
  const [revokeConfirm, setRevokeConfirm] = useState<SuperAdmin | null>(null)
  const [errorModal, setErrorModal] = useState<{ title: string; message: string } | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const revokeMutation = useRevokeSuperAdmin()
  const updatePermissionsMutation = useUpdateSuperAdminPermissions()
  const superAdminCount = superAdmins?.length ?? 0

  const confirmRevoke = async () => {
    if (!revokeConfirm) return
    try {
      await revokeMutation.mutateAsync(revokeConfirm.id)
      setRevokeConfirm(null)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao revogar super admin'
      setErrorModal({ title: 'Erro', message: msg })
      setRevokeConfirm(null)
    }
  }

  const togglePermission = async (sa: SuperAdmin, key: keyof SuperAdminPermissions) => {
    try {
      await updatePermissionsMutation.mutateAsync({
        id: sa.id,
        permissions: { [key]: !sa.permissions[key] },
      })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao atualizar permissão'
      setErrorModal({ title: 'Erro', message: msg })
    }
  }

  return (
    <div className="flex flex-col h-full">
      <SuperAdminHeader
        title="Super Admins"
        subtitle={
          loadError
            ? 'Erro ao carregar administradores da plataforma'
            : `${superAdminCount} administrador${superAdminCount === 1 ? '' : 'es'} da plataforma`
        }
        onMenuToggle={onMenuToggle}
        actions={
          <button
            onClick={() => setShowAddModal(true)}
            aria-label="Adicionar Super Admin"
            title="Adicionar Super Admin"
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Adicionar Super Admin</span>
          </button>
        }
      />

      <div className="flex-1 overflow-auto p-4 md:p-5 max-w-7xl mx-auto w-full">
        <div className="mb-5 rounded-lg border border-teal-100 bg-teal-50/70 px-4 py-3 text-sm text-teal-800">
          Esta lista usa a tabela autoritativa <strong>super_admins</strong>. Usuários comuns,
          admins de organização ou profissionais só aparecem aqui depois de promovidos.
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : loadError ? (
          <div className="bg-white rounded-lg border border-red-100 p-8 text-center">
            <ShieldCheck className="w-12 h-12 text-red-200 mx-auto mb-3" />
            <p className="font-medium text-gray-900">Não foi possível carregar os Super Admins.</p>
            <p className="mt-1 text-sm text-gray-500">
              {loadError instanceof Error ? loadError.message : 'A API retornou erro ao buscar a listagem.'}
            </p>
            <button
              onClick={() => void refetch()}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm text-white transition-colors hover:bg-teal-700"
            >
              Tentar novamente
            </button>
          </div>
        ) : !superAdmins?.length ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Nenhum super admin cadastrado.</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700"
            >
              <Plus className="w-4 h-4" />
              Adicionar Super Admin
            </button>
          </div>
        ) : (
          <SuperAdminsGrid
            superAdmins={superAdmins}
            currentProfileId={userProfile?.id}
            expandedId={expandedId}
            onToggleExpanded={(id) => setExpandedId(expandedId === id ? null : id)}
            onRevoke={setRevokeConfirm}
            onTogglePermission={togglePermission}
          />
        )}
      </div>

      <ConfirmModal
        isOpen={!!revokeConfirm}
        onClose={() => setRevokeConfirm(null)}
        onConfirm={confirmRevoke}
        title="Revogar Super Admin"
        message={`Tem certeza que deseja revogar os privilégios de super admin de ${revokeConfirm?.name || revokeConfirm?.email}?`}
        confirmText="Revogar"
        variant="danger"
        isLoading={revokeMutation.isPending}
      />

      <AlertModal
        isOpen={!!errorModal}
        onClose={() => setErrorModal(null)}
        title={errorModal?.title || 'Erro'}
        message={errorModal?.message || ''}
        variant="error"
      />

      {showAddModal && (
        <AddSuperAdminModal
          onClose={() => setShowAddModal(false)}
          onError={(msg) => setErrorModal({ title: 'Erro', message: msg })}
        />
      )}
    </div>
  )
}

function SuperAdminsGrid({
  superAdmins,
  currentProfileId,
  expandedId,
  onToggleExpanded,
  onRevoke,
  onTogglePermission,
}: {
  superAdmins: SuperAdmin[]
  currentProfileId?: string
  expandedId: string | null
  onToggleExpanded: (id: string) => void
  onRevoke: (superAdmin: SuperAdmin) => void
  onTogglePermission: (superAdmin: SuperAdmin, key: keyof SuperAdminPermissions) => void
}) {
  const permissionKeys = Object.keys(PERMISSION_LABELS) as Array<keyof SuperAdminPermissions>

  const permissionSummary = (sa: SuperAdmin) => {
    const enabled = permissionKeys.filter((key) => sa.permissions[key]).length
    return `${enabled}/${permissionKeys.length} permissões`
  }

  return (
    <div className="overflow-visible rounded-lg border border-gray-200 bg-white">
      <div className="divide-y divide-gray-100 md:hidden">
        {superAdmins.map((sa) => {
          const isCurrentUser = sa.profile_id === currentProfileId
          return (
            <div key={sa.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-100">
                    <ShieldCheck className="h-4 w-4 text-teal-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-medium text-gray-900">{sa.name || sa.email}</p>
                      {isCurrentUser && (
                        <span className="rounded-full bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700">
                          Você
                        </span>
                      )}
                    </div>
                    {sa.name && <p className="mt-0.5 truncate text-xs text-gray-500">{sa.email}</p>}
                    <p className="mt-1 text-xs text-gray-400">
                      Desde {new Date(sa.created_at).toLocaleDateString('pt-BR')} · {permissionSummary(sa)}
                    </p>
                  </div>
                </div>

                <SuperAdminRowActions
                  superAdmin={sa}
                  isCurrentUser={isCurrentUser}
                  expanded={expandedId === sa.id}
                  onToggleExpanded={onToggleExpanded}
                  onRevoke={onRevoke}
                />
              </div>

              {expandedId === sa.id && (
                <PermissionsPanel superAdmin={sa} onTogglePermission={onTogglePermission} />
              )}
            </div>
          )
        })}
      </div>

      <div className="hidden md:block">
        <table className="w-full table-fixed">
          <thead className="border-b border-gray-200 bg-gray-50/80">
            <tr>
              <th className="w-[38%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Super Admin
              </th>
              <th className="w-[22%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Permissões
              </th>
              <th className="hidden w-[18%] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 lg:table-cell">
                Criado em
              </th>
              <th className="w-[22%] px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {superAdmins.map((sa) => {
              const isCurrentUser = sa.profile_id === currentProfileId
              return (
                <Fragment key={sa.id}>
                  <tr className="transition-colors hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-100">
                          <ShieldCheck className="h-4 w-4 text-teal-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="truncate font-medium text-gray-900">{sa.name || sa.email}</p>
                            {isCurrentUser && (
                              <span className="shrink-0 rounded-full bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700">
                                Você
                              </span>
                            )}
                          </div>
                          {sa.name && <p className="truncate text-sm text-gray-500">{sa.email}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                        {permissionSummary(sa)}
                      </span>
                    </td>
                    <td className="hidden px-4 py-4 text-sm text-gray-500 lg:table-cell">
                      {new Date(sa.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <SuperAdminRowActions
                        superAdmin={sa}
                        isCurrentUser={isCurrentUser}
                        expanded={expandedId === sa.id}
                        onToggleExpanded={onToggleExpanded}
                        onRevoke={onRevoke}
                      />
                    </td>
                  </tr>
                  {expandedId === sa.id && (
                    <tr>
                      <td colSpan={4} className="border-t border-gray-100 bg-gray-50/50 px-4 py-4">
                        <PermissionsPanel superAdmin={sa} onTogglePermission={onTogglePermission} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function SuperAdminRowActions({
  superAdmin,
  isCurrentUser,
  expanded,
  onToggleExpanded,
  onRevoke,
}: {
  superAdmin: SuperAdmin
  isCurrentUser: boolean
  expanded: boolean
  onToggleExpanded: (id: string) => void
  onRevoke: (superAdmin: SuperAdmin) => void
}) {
  return (
    <div className="flex items-center justify-end gap-2">
      <button
        onClick={() => onToggleExpanded(superAdmin.id)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50"
        aria-label={expanded ? 'Ocultar permissões' : 'Ver permissões'}
        title={expanded ? 'Ocultar permissões' : 'Ver permissões'}
      >
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      <button
        onClick={() => onRevoke(superAdmin)}
        disabled={isCurrentUser}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-100 text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:border-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent"
        aria-label="Revogar super admin"
        title={isCurrentUser ? 'Você não pode revogar seu próprio acesso' : 'Revogar super admin'}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  )
}

function PermissionsPanel({
  superAdmin,
  onTogglePermission,
}: {
  superAdmin: SuperAdmin
  onTogglePermission: (superAdmin: SuperAdmin, key: keyof SuperAdminPermissions) => void
}) {
  return (
    <div className="mt-4 border-t border-gray-100 pt-3 md:mt-0 md:border-t-0 md:pt-0">
      <p className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500">Permissões</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {(Object.keys(PERMISSION_LABELS) as Array<keyof SuperAdminPermissions>).map((key) => (
          <div
            key={key}
            className="flex items-center gap-3 rounded-lg p-2 hover:bg-white md:hover:bg-gray-100"
          >
            <button
              type="button"
              onClick={() => onTogglePermission(superAdmin, key)}
              className={`flex h-5 w-9 shrink-0 rounded-full transition-colors ${
                superAdmin.permissions[key] ? 'bg-teal-500' : 'bg-gray-200'
              }`}
              aria-label={PERMISSION_LABELS[key]}
              aria-pressed={superAdmin.permissions[key]}
            >
              <span
                className={`mt-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                  superAdmin.permissions[key] ? 'translate-x-4' : 'translate-x-0.5'
                }`}
              />
            </button>
            <span className="text-sm text-gray-700">{PERMISSION_LABELS[key]}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function AddSuperAdminModal({
  onClose,
  onError,
}: {
  onClose: () => void
  onError: (msg: string) => void
}) {
  const [mode, setMode] = useState<'existing' | 'new'>('existing')
  const [search, setSearch] = useState('')
  const [searchResults, setSearchResults] = useState<{ id: string; email: string; name: string | null }[]>([])
  const [selectedUser, setSelectedUser] = useState<{ id: string; email: string; name: string | null } | null>(null)
  const [searching, setSearching] = useState(false)
  const [newForm, setNewForm] = useState({ email: '', full_name: '' })
  const [createdPassword, setCreatedPassword] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const promoteMutation = usePromoteSuperAdmin()
  const createMutation = useCreateSuperAdmin()

  const handleSearch = async (q: string) => {
    setSearch(q)
    if (q.length < 2) {
      setSearchResults([])
      return
    }
    setSearching(true)
    try {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, email, full_name')
        .or(`full_name.ilike.%${q}%,email.ilike.%${q}%`)
        .limit(20)

      const profileIds = (profiles || []).map((profile: { id: string }) => profile.id)

      let superAdminProfileIds = new Set<string>()
      if (profileIds.length > 0) {
        const { data: superAdmins } = await supabase
          .from('super_admins')
          .select('profile_id')
          .in('profile_id', profileIds)

        superAdminProfileIds = new Set(
          (superAdmins || []).map((superAdmin: { profile_id: string }) => superAdmin.profile_id)
        )
      }

      setSearchResults(
        (profiles || [])
          .filter((profile: { id: string }) => !superAdminProfileIds.has(profile.id))
          .slice(0, 10)
          .map((profile: { id: string; email: string; full_name: string | null }) => ({
            id: profile.id,
            email: profile.email,
            name: profile.full_name,
          }))
      )
    } finally {
      setSearching(false)
    }
  }

  const handlePromote = async () => {
    if (!selectedUser) return
    try {
      await promoteMutation.mutateAsync({ profile_id: selectedUser.id })
      onClose()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao promover usuário'
      onError(msg)
      onClose()
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const result = await createMutation.mutateAsync(newForm)
      setCreatedPassword(result.temporaryPassword)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao criar super admin'
      onError(msg)
      onClose()
    }
  }

  const handleCopy = async () => {
    if (!createdPassword) return
    try {
      await navigator.clipboard.writeText(createdPassword)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* noop */
    }
  }

  if (createdPassword) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-green-700">Super Admin Criado!</h3>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 space-y-4">
            <p className="text-sm text-gray-600">
              O usuário <strong>{newForm.email}</strong> foi criado com sucesso como super admin.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha temporária</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-100 rounded-lg font-mono text-sm break-all select-all">
                  {createdPassword}
                </code>
                <button
                  onClick={handleCopy}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Copiar senha"
                >
                  {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">Informe essa senha ao usuário para o primeiro acesso.</p>
            </div>
          </div>
          <div className="p-4 border-t border-gray-100 flex justify-end">
            <button onClick={onClose} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
              Fechar
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Adicionar Super Admin</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setMode('existing')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              mode === 'existing'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Usuário existente
          </button>
          <button
            onClick={() => setMode('new')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              mode === 'new'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Criar novo
          </button>
        </div>

        {mode === 'existing' ? (
          <div className="p-4 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Buscar por nome ou email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {searching && <p className="text-sm text-gray-400 text-center">Buscando...</p>}

            {searchResults.length > 0 && !selectedUser && (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-52 overflow-y-auto">
                {searchResults.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => setSelectedUser(u)}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
                      {(u.name || u.email).charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{u.name || u.email}</p>
                      {u.name && <p className="text-xs text-gray-500">{u.email}</p>}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {selectedUser && (
              <div className="flex items-center gap-3 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-xs font-medium text-teal-700">
                  {(selectedUser.name || selectedUser.email).charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{selectedUser.name || selectedUser.email}</p>
                  {selectedUser.name && <p className="text-xs text-gray-500">{selectedUser.email}</p>}
                </div>
                <button onClick={() => setSelectedUser(null)} className="p-1 text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
                Cancelar
              </button>
              <button
                onClick={handlePromote}
                disabled={!selectedUser || promoteMutation.isPending}
                className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {promoteMutation.isPending ? 'Promovendo...' : 'Promover a Super Admin'}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleCreate} className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo *</label>
              <input
                type="text"
                required
                value={newForm.full_name}
                onChange={(e) => setNewForm((p) => ({ ...p, full_name: e.target.value }))}
                placeholder="Nome do super admin"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                required
                value={newForm.email}
                onChange={(e) => setNewForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="email@exemplo.com"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {createMutation.isPending ? 'Criando...' : 'Criar Super Admin'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
